import { AlertTriangle, FileText } from "lucide-react";
import type { components } from "@dolfin/api-public";
import type { DolfinClient } from "@/components/DolfinProvider";
import type { TaxRateRow } from "@/hooks/useInvoiceResources";
import type { DiscountType } from "@/components/Discount";
import { cn } from "@/lib/utils";

type ExtractedContractTerms = components["schemas"]["ExtractedContractTerms"];
type ProductResponse = components["schemas"]["ProductResponse"];

/** Average of all per-field confidence scores (0..1), or null if none were returned. */
export function averageConfidence(terms: ExtractedContractTerms): number | null {
  const scores = Object.values(terms.confidence ?? {})
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n));
  if (scores.length === 0) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

/** Humanise a camelCase / dotted field key for display (e.g. "customerName" → "Customer name"). */
export function humaniseField(key: string): string {
  const last = key.split(".").pop() ?? key;
  const spaced = last.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

function confidenceTone(pct: number): string {
  if (pct >= 80) return "text-emerald-600 dark:text-emerald-400";
  if (pct >= 50) return "text-amber-600 dark:text-amber-400";
  return "text-destructive";
}

export function ContractExtractBanner({ terms }: { terms: ExtractedContractTerms }) {
  const avg = averageConfidence(terms);
  const pct = avg == null ? null : Math.round(avg * 100);
  const missing = terms.missingFields ?? [];

  return (
    <div className="rounded-xl border border-accent-blue/30 bg-accent-blue/5 p-4">
      <div className="flex items-start gap-3">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg border border-accent-blue/25 bg-accent-blue/10 text-accent-blue">
          <FileText className="size-4" />
        </span>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-accent-blue">
                Prefilled from contract
              </p>
              <p className="mt-1 text-xs text-pretty text-muted-foreground">
                Check every field below before you create the recurring invoice. Read values can be
                wrong.
              </p>
            </div>
            {pct != null && (
              // A bar reads "trust this" versus "check this" faster than a bare number.
              <div className="w-28 shrink-0" aria-label={`${pct}% confidence`}>
                <div className="flex items-baseline justify-between text-[10px] text-muted-foreground">
                  <span>Confidence</span>
                  <span className={cn("font-semibold tabular-nums", confidenceTone(pct))}>{pct}%</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      "h-full rounded-full",
                      pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-destructive",
                    )}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          {missing.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              <span>
                <span className="font-medium">Not found in the contract: </span>
                {missing.map((f) => humaniseField(f)).join(", ")}. Fill these in by hand.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export interface ContractScheduleDefaults {
  customerId: string;
  /** Display name for the matched customer, "" when unmatched. */
  customerLabel: string;
  /** Text to seed the customer search box with when no exact match was found. */
  customerSeedQuery: string;
  currency: string;
  memo: string;
  startDate: string;
  cadence: string;
  totalOccurrences: number | null;
  autoSend: boolean;
  lineItems: {
    productId: string;
    quantity: number;
    taxRateId: string | null;
    unitPriceOverride: number | null;
    productName: string | null;
    // Required by the form schema though a contract states no discounts; omitting it silently fails validation.
    discountType: DiscountType;
    discountValue: number | null;
  }[];
}

function toNumberOrNull(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Customer matched by email first; unmatched products keep their contract name for the search box. */
export async function applyContractTerms(
  terms: ExtractedContractTerms,
  opts: {
    api: DolfinClient;
    products: ProductResponse[];
    taxRates: TaxRateRow[];
    defaultCurrency: string | null;
  },
): Promise<ContractScheduleDefaults> {
  const { api, taxRates, defaultCurrency } = opts;

  const customerQuery = (terms.customerEmail || terms.customerName || "").trim();
  let customerId = "";
  let customerLabel = "";
  if (customerQuery) {
    const { data } = await api.GET("/v1/customers/search", {
      params: { query: { q: customerQuery, limit: 5 } },
    });
    const hits = data ?? [];
    const email = terms.customerEmail?.trim().toLowerCase();
    const exactEmail = email
      ? hits.find((h) => h.email?.trim().toLowerCase() === email)
      : undefined;
    const match = exactEmail ?? (hits.length === 1 ? hits[0] : undefined);
    if (match?.id) {
      customerId = match.id;
      customerLabel = match.name;
    }
  }

  const matchTaxRate = (name: string | null | undefined): string | null => {
    if (!name) return null;
    const target = name.trim().toLowerCase();
    return taxRates.find((r) => r.name.trim().toLowerCase() === target)?.id ?? null;
  };

  const extractedItems = terms.lineItems ?? [];
  const lineItems = await Promise.all(
    extractedItems.map(async (li) => {
      const productName = li.productName?.trim() || null;
      let productId = "";
      if (productName) {
        try {
          const { data } = await api.GET("/v1/products/search", {
            params: { query: { q: productName, limit: 5 } },
          });
          const target = productName.toLowerCase();
          const exact = (data ?? []).find(
            (h) => h.name.trim().toLowerCase() === target,
          );
          if (exact?.id) productId = exact.id;
        } catch {
          // search is best-effort; leave unmatched for manual selection
        }
      }
      return {
        productId,
        // A contract line is one unit unless it states otherwise.
        quantity: toNumberOrNull(li.quantity) ?? 1,
        taxRateId: matchTaxRate(li.taxRateName),
        unitPriceOverride: toNumberOrNull(li.unitPriceOverride),
        productName,
        discountType: "None" as const,
        discountValue: null,
      };
    }),
  );

  return {
    customerId,
    customerLabel,
    customerSeedQuery: customerId ? "" : customerQuery,
    currency: terms.currency || defaultCurrency || "",
    memo: terms.memo ?? "",
    startDate: terms.startDate ? terms.startDate.slice(0, 10) : "",
    cadence: terms.cadence ?? "Monthly",
    totalOccurrences: toNumberOrNull(terms.totalOccurrences),
    autoSend: false,
    lineItems:
      lineItems.length > 0
        ? lineItems
        : [
            {
              productId: "",
              quantity: 1,
              taxRateId: null,
              unitPriceOverride: null,
              productName: null,
              discountType: "None" as const,
              discountValue: null,
            },
          ],
  };
}
